import React, { useEffect, useState, useRef, useContext } from 'react';
import { Space, Table, Button, Input, Modal, Select, message } from 'antd';
import {
  SearchOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

import IconGroup from './IconGroup';
import CreateModalContent from './CreateModalContent';
import { LoadingIcon } from '../../../components';
import {
  getSites,
  getCountries,
  putSite,
  putSiteStatus,
  postSite,
  deleteSite,
} from '../../../utils/api';
import { useLocalStorage } from '../../../hooks';
import { UserContext } from '../../../context/UserContext';

const SiteTable = ({ currentOrg, setCurrentOrg, orgData, cropData }) => {
  const [data, setData] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [countryData, setCountryData] = useLocalStorage('countryData', null);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  // const [currentOrg, setCurrentOrg] = useState(null);
  // const [orgData, setOrgData] = useState([]);
  // const [cropData, setCropData] = useState(null);
  const searchInput = useRef(null);
  const { userData } = useContext(UserContext);

  const showCreateModal = () => {
    setCreateModalOpen(true);
  };

  const handleCreateCancel = () => {
    setCreateModalOpen(false);
  };

  const renderBoolean = (bool) => (
    <p className={`${bool ? 'text-primaryBlue' : 'text-dangerRed'}`}>
      {String(bool)}
    </p>
  );

  // START - API Functions
  const fetchSites = async (orgId) => {
    try {
      setDataLoading(true);
      const res = await getSites(orgId);
      const data = res.data?.data?.map((site) => {
        return {
          ...site,
          key: site.id,
        };
      });
      console.log({ data });
      setData(data);
    } catch (err) {
      message.error('Error fetching Sites!');
    } finally {
      setDataLoading(false);
    }
  };

  const updateSite = async (id, data) => {
    try {
      console.log({ id });
      const res = await putSite(id, data);
      const res2 = await fetchSites(currentOrg);
      console.log({ res });
    } catch (err) {
      message.error('Error updating Site!');
    }
  };

  const hideSite = async (id, data) => {
    try {
      const res = await putSiteStatus(id, data);
      const res2 = await fetchSites(currentOrg);
      console.log({ res });
    } catch (err) {
      message.error('Error hiding Site!');
    }
  };

  const createSite = async (newData) => {
    try {
      const reqBody = { ...newData, organization: currentOrg };
      const res = await postSite(reqBody);
      const res2 = await fetchSites(currentOrg);
      console.log({ res });
    } catch (err) {
      message.error('Error creating Site!');
    }
  };

  const removeSite = async (id) => {
    try {
      const res = await deleteSite(id);
      const res2 = await fetchSites(currentOrg);
      console.log({ res });
    } catch (err) {
      message.error('Error deleting Site!');
    }
  };
  // END - API Functions

  // Fetch orgs on component mount
  // useEffect(() => {
  //   if (userData) {
  //     if (userData.is_superuser) {
  //       fetchOrgs();
  //     } else {
  //       setCurrentOrg(userData?.organization_id);
  //     }
  //   }
  // }, [userData]);

  // Fetch Site on org Change
  useEffect(() => {
    if (currentOrg) {
      fetchSites(currentOrg);
    }
  }, [currentOrg]);

  // Fetch countries and crops on page load
  useEffect(() => {
    const fn = async () => {
      const res = await getCountries();
      const countries = res?.data?.data || [];
      console.log({ countries });
      setCountryData(
        countries.map((country) => ({
          value: country.name,
          label: `${country.unicodeFlag} ${country.name}`,
          key: country.name,
        }))
      );
    };
    if (!countryData) {
      fn();
    }

    // fetchCrops();
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Display Name',
      dataIndex: 'display_name',
      key: 'display_name',
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: 'City',
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: 'Environment',
      dataIndex: 'growing_environment',
      key: 'env',
    },
    {
      title: 'Latitude',
      dataIndex: 'lat',
      key: 'lat',
    },
    {
      title: 'Longitude',
      dataIndex: 'long',
      key: 'long',
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (value) =>
        !!value ? (
          <CheckCircleOutlined className='text-successGreen' />
        ) : (
          <CloseCircleOutlined className='text-dangerRed' />
        ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_, record) => (
        <IconGroup
          ele={record}
          updateCallback={updateSite}
          hideCallback={hideSite}
          deleteCallback={removeSite}
          countryData={countryData}
          cropData={cropData}
        />
      ),
    },
  ];

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };
  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: 'block',
          }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{
              width: 90,
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size='small'
            style={{
              width: 90,
            }}
          >
            Reset
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              confirm({
                closeDropdown: true,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
          <Button
            type='link'
            size='small'
            onClick={() => {
              close();
            }}
          >
            Close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? '#39f' : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const finalColumns = columns.map((vals) => {
    const obj =
      vals.key === 'actions'
        ? { ...vals }
        : { ...vals, ...getColumnSearchProps(vals.key) };
    return obj;
  });

  return (
    <div>
      {dataLoading ? (
        <LoadingIcon classname='absolute h-full top-[40vh]' height="200px" />
      ) : (
        <>
          <div
            className={`w-full pb-10 h-fit flex ${
              userData?.is_superuser ? 'justify-between' : 'justify-end'
            } items-center`}
          >
            {userData?.is_superuser ? (
              <Select
                className='min-w-[150px] mr-5'
                placeholder='Select Organization'
                value={currentOrg}
                showSearch
                filterOption={(input, option) =>
                  (option?.label || "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                onChange={setCurrentOrg}
                options={orgData.map((org) => ({
                  value: org.key,
                  label: org.name,
                }))}
              />
            ) : null}
            {(userData?.is_superuser || userData?.is_admin) && (
              <Button type='primary' onClick={() => setCreateModalOpen(true)}>
                Create Site
              </Button>
            )}
          </div>
          <Table
            dataSource={data}
            columns={finalColumns}
            pagination={{
              hideOnSinglePage: true,
              position: ['bottomCenter'],
            }}
          />
        </>
      )}
      <Modal
        maskClosable={false}
        title={
          <div className='mb-5 font-IBMPlexSansBold text-2xl'>Create Site</div>
        }
        open={createModalOpen}
        onCancel={handleCreateCancel}
        footer={null}
      >
        <CreateModalContent
          countryData={countryData}
          setOpen={setCreateModalOpen}
          createCallback={createSite}
          cropData={cropData}
        />
      </Modal>
    </div>
  );
};

export default SiteTable;
